import { skillDefByName } from '../data/guide/skills'
import { classItems } from './classBuilds'
import { isLearnSection } from './skillItems'
import { spriteForItem } from './itemSprites'
import { staffItemImage } from '../data/guide/itemImages'
import { itemName } from './worldFormat'
import type { ClassItem } from './classBuilds'
import type { SkillDef } from '../data/guide/skills'
import type { CharacterClass } from '../types/progression'
import type { Item, WorldData } from '../types/world'

/**
 * Estados de la relación habilidad → ítem, separados (la carga real de la imagen se mide aparte).
 * - conectado: ítem de aprendizaje identificado por ID, compatible con la clase y con imagen asignada.
 * - sin-imagen: ítem identificado, pero sin imagen asignada.
 * - rota: el ítem existe en la exportación, pero algo no cuadra (clase o skill_id): se muestra con aviso.
 * - equipo / mascota: el poder lo da un arma o una mascota con ese skill_id.
 * - pendiente: no hay relación documentada en la exportación.
 */
export type SkillLinkStatus = 'conectado' | 'sin-imagen' | 'rota' | 'equipo' | 'mascota' | 'pendiente'

export type SkillLink = {
  skillName: string
  def?: SkillDef
  status: SkillLinkStatus
  /** Cómo se estableció la relación. */
  method?: 'staff' | 'skill_id' | 'nombre'
  learn: ClassItem[]
  origin: ClassItem[]
  /** Hay más ítems de origen de los que se muestran. */
  originTotal: number
  problems: string[]
}

const compact = (s: string) =>
  s
    .toLowerCase()
    .replace(/^(scroll|orb|parchment|book)\s+of\s+/, '')
    .replace(/\s+(parchment|scroll|book)$/, '')
    .replace(/[^a-z0-9]/g, '')

const toClassItem = (w: WorldData, id: string, item: Item): ClassItem => ({ id, name: itemName(w, id), item, section: Number(id.split(',')[0]) })

export const hasAssignedImage = (id: string) => !!staffItemImage(id) || !!spriteForItem(id)

const PET_SECTION = 13
const ORIGIN_LIMIT = 4

export function resolveSkillLink(w: WorldData, cls: CharacterClass, skillName: string): SkillLink {
  const def = skillDefByName(skillName)
  const { items } = classItems(w, cls)
  const problems: string[] = []
  const base = { skillName, def, origin: [] as ClassItem[], originTotal: 0, problems }

  const finish = (learn: ClassItem[], method: SkillLink['method']): SkillLink => {
    const broken = problems.length > 0
    const status: SkillLinkStatus = broken ? 'rota' : learn.every((l) => hasAssignedImage(l.id)) ? 'conectado' : 'sin-imagen'
    return { ...base, status, method, learn }
  }

  // 1) Relación indicada por el staff: ítem por ID exacto, validando clase y skill_id.
  if (def?.learnItemId) {
    const item = w.items.get(def.learnItemId)
    if (item) {
      const compatible = items.some((ci) => ci.id === def.learnItemId)
      if (!compatible) problems.push(`${item.name} (${def.learnItemId}) no figura como compatible con ${cls.base} en 05_item_classes.`)
      if (item.skillId && item.skillId !== def.skillId) problems.push(`${item.name} tiene skill_id ${item.skillId} en 01_items; se esperaba ${def.skillId}.`)
      return finish([toClassItem(w, def.learnItemId, item)], 'staff')
    }
    problems.push(`El ítem ${def.learnItemId} no está publicado en la exportación cargada.`)
  }

  // 2) Por skill_id entre los ítems compatibles con la clase.
  if (def) {
    const same = items.filter((ci) => ci.item.skillId === def.skillId)
    const learn = same.filter((ci) => isLearnSection(ci.section))
    if (learn.length) {
      problems.length = 0
      return finish(learn, 'skill_id')
    }
    const origin = same.filter((ci) => !isLearnSection(ci.section))
    if (origin.length) {
      const pets = origin.filter((ci) => ci.section === PET_SECTION)
      const shown = (pets.length ? pets : origin).slice(0, ORIGIN_LIMIT)
      return { ...base, status: pets.length ? 'mascota' : 'equipo', method: 'skill_id', learn: [], origin: shown, originTotal: (pets.length ? pets : origin).length }
    }
  }

  // 3) Por nombre exacto del poder (sin "Scroll of"/"Parchment"), solo en orbes y pergaminos compatibles.
  const names = [skillName, ...(def?.aliases ?? [])].map(compact)
  const byName = items.filter((ci) => isLearnSection(ci.section) && names.includes(compact(ci.item.name)))
  if (byName.length) {
    problems.length = 0
    return finish(byName, 'nombre')
  }

  return { ...base, status: problems.length ? 'rota' : 'pendiente', learn: [] }
}

export const STATUS_LABEL: Record<SkillLinkStatus, string> = {
  conectado: 'Ítem identificado · imagen asignada',
  'sin-imagen': 'Ítem identificado · sin imagen asignada',
  rota: 'Asociación por revisar',
  equipo: 'Se obtiene con equipo',
  mascota: 'Se obtiene con una mascota',
  pendiente: 'Relación pendiente de documentar',
}

export const METHOD_LABEL: Record<NonNullable<SkillLink['method']>, string> = {
  staff: 'skill_id e item_id facilitados por el staff · 11/10/2026',
  skill_id: 'Asociado por skill_id en 01_items',
  nombre: 'Asociado por el nombre exacto del ítem',
}
